/**
* @copyright Copyright (c) MITSUBISHI Electric Corporation. All rights reserved.
*/

import React, { useState, useContext, useRef, useEffect } from 'react';
import validate from 'validate.js'
import { IndexContext } from '../IndexProvider';
import PAGES from '../consts/pages';

const confirmCodeConstraints = {
    confirmCode: {
        presence: {
            allowEmpty: false
        },
        format: {
            pattern: /^[0-9]{6}$/
        }
    }
};

const passwordConstraints = {
    password: {
        presence: {
            allowEmpty: false
        }
    }
};

const confirmedPasswordConstraints = {
    confirmedPassword: {
        presence: {
            allowEmpty: false
        }
    }
};

export default function ResetPassword() {

    const [confirmCode, setConfirmCode] = useState("")
    const [password, setPassword] = useState("")
    const [confirmedPassword, setConfirmedPassword] = useState("");

    const [isConfirmCodeValid, setIsConfirmCodeValid] = useState(false)
    const [isPasswordValid, setIsPasswordValid] = useState(false)
    const [isConfirmedPasswordValid, setIsConfirmedPasswordValid] = useState(false)

    const [shouldShowConfirmCodeFeedback, setShouldShowConfirmCodeFeedback] = useState(false)
    const [shouldShowPasswordFeedback, setShouldShowPasswordFeedback] = useState(false)
    const [shouldShowConfirmedPasswordFeedback, setShouldShowConfirmedPasswordFeedback] = useState(false)

    const [isPending, setIsPending] = useState(false)
    const [isReadOnly, setIsReadOnly] = useState(true)

    useEffect(() => {
        if (password !== confirmedPassword || validate({ confirmedPassword: confirmedPassword }, confirmedPasswordConstraints)) {
            setIsConfirmedPasswordValid(false)
        } else {
            setIsConfirmedPasswordValid(true)
        }
    }, [confirmedPassword])

    const { setCurrentPage, generateConfirmCode, currentChangeAccountId, confirmPassword } = useContext(IndexContext)

    const onSubmit = async (event) => {
        event.preventDefault();
        event.stopPropagation();

        let count = 0

        if (validate({ confirmCode: confirmCode }, confirmCodeConstraints)) {
            setIsConfirmCodeValid(false)
        } else {
            setIsConfirmCodeValid(true)
            count++
        }

        if (validate({ password: password }, passwordConstraints)) {
            setIsPasswordValid(false)
        } else {
            setIsPasswordValid(true)
            count++
        }

        if (validate({ confirmedPassword: password }, confirmedPasswordConstraints)) {
            setIsConfirmedPasswordValid(false)
        } else {
            setIsConfirmedPasswordValid(true)
            count++
        }

        if (count === 3) {
            setIsPending(true)
            await confirmPassword(password, confirmCode)
            setIsPending(false)
        }
    };

    const onConfirmCodeChange = (event) => {
        setConfirmCode(event.target.value)
        setShouldShowConfirmCodeFeedback(true)
        if (validate({ confirmCode: event.target.value }, confirmCodeConstraints)) {
            setIsConfirmCodeValid(false)
        } else {
            setIsConfirmCodeValid(true)
        }
    }

    const onPasswordChange = (event) => {
        setPassword(event.target.value)
        setShouldShowPasswordFeedback(true)
        if (validate({ password: event.target.value }, passwordConstraints)) {
            setIsPasswordValid(false)
        } else {
            setIsPasswordValid(true)
        }
    }

    const onConfirmedPasswordChange = (event) => {
        setConfirmedPassword(event.target.value)
        setShouldShowConfirmedPasswordFeedback(true)
    }

    const buildFormControlClasses = (shouldShowFeedback, isValid) => {
        if (shouldShowFeedback) {
            return `form-control ${isValid ? "is-valid" : "is-invalid"}`
        } else {
            return "form-control"
        }
    }

    const onLoginLinkClick = (e) => {
        e.preventDefault()
        setCurrentPage(PAGES.LOGIN)
    }

    const onRegenerateConfirmCodeLinkClick = (e) => {
        e.preventDefault()
        generateConfirmCode(currentChangeAccountId)
    }

    const onConfirmCodeFocus = (e) => {
        setIsReadOnly(false)
    }

    return (
        <div className="d-flex justify-content-center align-items-center ps-3 pe-3 pt-5 pb-5">
            <form noValidate className="w-100 form-container p-3" style={{ border: "1px solid #0073e6" }} onSubmit={onSubmit} >
                <div className="mb-3 d-flex justify-content-center align-items-center" style={{ backgroundColor: "#0073e6", fontSize: 24, fontWeight: "bold", color: "#fff" }}>
                    <label style={{ marginBottom: 0 }}>パスワードリセット</label>
                </div>

                <div className="mb-3" >
                    <label htmlFor="confirmCodeInput">検証コード</label>
                    {
                        isReadOnly ?
                            <input readOnly className={buildFormControlClasses(shouldShowConfirmCodeFeedback, isConfirmCodeValid)} onChange={onConfirmCodeChange} onFocus={onConfirmCodeFocus} value={confirmCode} type="text" id="confirmCodeInput" required />
                            :
                            <input className={buildFormControlClasses(shouldShowConfirmCodeFeedback, isConfirmCodeValid)} onChange={onConfirmCodeChange} onFocus={onConfirmCodeFocus} value={confirmCode} type="text" id="confirmCodeInput" required />
                    }
                    <div className="invalid-feedback">
                        英数字６桁を入力してください。
                    </div>
                </div>


                <div className="mb-3" >
                    <label htmlFor="passwordInput">新パスワード</label>
                    <input className={buildFormControlClasses(shouldShowPasswordFeedback, isPasswordValid)} onChange={onPasswordChange} value={password} type="password" id="passwordInput" required />
                    <div className="invalid-feedback">
                        新パスワードを入力してください。
                    </div>
                </div>

                <div className="mb-3">
                    <label htmlFor="confirmPasswordInput">新パスワード（確認）</label>
                    <input className={buildFormControlClasses(shouldShowConfirmedPasswordFeedback, isConfirmedPasswordValid)} onChange={onConfirmedPasswordChange} value={confirmedPassword} type="password" id="confirmPasswordInput" required />
                    <div className="invalid-feedback" >
                        入力されたパスワードが一致しません。
                    </div>
                </div>

                <div className="d-flex align-items-start justify-content-between" >
                    <button className="btn btn-primary" type="submit" >
                        {isPending ? "リセット中..." : "リセット"}
                    </button>
                    <div className="d-flex flex-column align-items-start justify-content-end" >
                        <a style={{ cursor: "pointer", fontSize: 12 }} onClick={onLoginLinkClick} >ログイン</a>
                        <a style={{ cursor: "pointer", fontSize: 12 }} onClick={onRegenerateConfirmCodeLinkClick} >検証コード再発行</a>
                    </div>
                </div>
            </form>
        </div>
    );
}
